/**
 * A metric split by the channel that acquired the customer.
 *
 * Shared by the Refunds and Churn pages, which ask the same question of
 * different data: refunds per source, retention per source. Both come from the
 * same bridge — Manifold billing joined to BigQuery attribution on email — and
 * both inherit its two honesty problems, which is why they inherit one
 * component rather than each solving them separately.
 *
 * UNATTRIBUTED IS A ROW, NOT A GAP. The backend returns it as a bucket, and it
 * is rendered as one, greyed. Dropping it would make the bars sum to less than
 * the total printed beside them, and nobody would be able to tell which channel
 * went missing.
 *
 * A THIN COHORT SAYS SO. Splitting a subscriber base four or five ways leaves
 * cohorts too small to read a rate from. `sufficient: false` renders the row
 * muted with its count shown, so a reader sees the number is thin rather than
 * inferring a trend from three events.
 */

import { AlertCircle, Loader2, Info } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from './ui'

/**
 * Labels and colours come from lib/channels, the one registry both this card
 * and every page that draws a channel now read. This file used to keep its own
 * pair of maps, which disagreed with CrossSellPage on Meta and Google and knew
 * `organic_search` where the backend emits `organic`.
 *
 * Imported AND re-exported: this file uses both below, and several pages
 * import them from here. A bare `export ... from` would re-export without
 * binding them locally, which fails only at render time.
 */
import { channelLabel, channelColor } from '../lib/channels'
export { channelLabel, channelColor }

/**
 * @param {object}   props
 * @param {string}   props.title
 * @param {object[]} props.rows       [{ channel, value, secondary, sufficient }]
 * @param {function} props.formatValue
 * @param {string}   [props.valueLabel]
 * @param {object}   [props.coverage] backend coverage counters
 * @param {string}   [props.coverageNote]
 * @param {boolean}  props.loading
 * @param {string}   [props.error]
 * @param {string}   [props.emptyMessage]
 * @param {object}   [props.action]   rendered at the header's right, e.g. AttributionSettings
 */
export default function ChannelSplitCard({
  title, rows = [], formatValue, valueLabel, coverage, coverageNote,
  loading, error, emptyMessage = 'No data for this period', action = null,
}) {
  const max = rows.reduce((m, r) => Math.max(m, Math.abs(r.value) || 0), 0)

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{title}</CardTitle>
          {action}
        </div>
        {coverageNote && (
          <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{coverageNote}</span>
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="h-40 flex flex-col items-center justify-center text-center gap-2">
            <AlertCircle className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground max-w-xs">{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          <>
            {valueLabel && (
              <div className="flex justify-between text-xs text-muted-foreground mb-2">
                <span>Channel</span>
                <span>{valueLabel}</span>
              </div>
            )}
            <div className="space-y-2.5">
              {rows.map(row => {
                const thin = row.sufficient === false
                return (
                  <div key={row.channel}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className={`flex items-center gap-2 min-w-0 ${thin ? 'text-muted-foreground' : ''}`}>
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: channelColor(row.channel) }}
                        />
                        <span className="truncate">{channelLabel(row.channel)}</span>
                        {thin && (
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                            thin
                          </span>
                        )}
                      </span>
                      <span className="flex items-baseline gap-2 shrink-0 tabular-nums">
                        <span className={thin ? 'text-muted-foreground' : 'font-medium'}>
                          {formatValue(row.value)}
                        </span>
                        {row.secondary && (
                          <span className="text-xs text-muted-foreground">{row.secondary}</span>
                        )}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: max > 0 ? `${Math.max(2, (Math.abs(row.value) / max) * 100)}%` : '0%',
                          backgroundColor: channelColor(row.channel),
                          opacity: thin ? 0.45 : 1,
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>

            {coverage && (
              <p className="mt-4 pt-3 border-t text-xs text-muted-foreground">
                {/* Two different failures, deliberately not merged: a customer
                    we cannot find is a broken join; a customer with no channel
                    is an attribution gap. They look identical in a total and
                    want opposite reactions. */}
                {coverage.matchedChannel != null && coverage.matchedPayment != null && (
                  <>
                    {coverage.matchedChannel} of {coverage.matchedPayment} matched refunds reached a channel
                    {coverage.matchedPayment !== coverage.matchedCustomer && (
                      <> · {coverage.matchedPayment - coverage.matchedCustomer} had no customer record</>
                    )}
                  </>
                )}
                {coverage.matchedCustomer != null && coverage.unmatched != null && (
                  <>
                    {coverage.matchedCustomer} attributed · {coverage.unmatched} unmatched
                    {coverage.skipped > 0 && <> · {coverage.skipped} undatable</>}
                  </>
                )}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
